import { useState } from "react";
import type { FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/da2jphiamq5jpe1hruf0";

export default function ContactSection() {
  const [status, setStatus] = useState<"idle" | "sending" | "success" | "error">("idle");
  const [message, setMessage] = useState("");

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new FormData(form);

    const honeypot = String(data.get("contact_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setMessage("已收到你的留言，感谢反馈！");
      form.reset();
      return;
    }
    data.delete("contact_alt");

    setStatus("sending");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(data as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = responseText ? JSON.parse(responseText) : null;
      } catch {
        parsed = null;
      }
      const code = parsed?.code;
      const serverMsg =
        (parsed?.meta as Record<string, unknown>)?.message ||
        (parsed?.message as string) ||
        (parsed?.meta as Record<string, unknown>)?.detail ||
        "";
      const text = typeof serverMsg === "string" ? serverMsg.toLowerCase() : "";

      if (res.ok && code === "OK" && !text.includes("spam")) {
        setStatus("success");
        setMessage("已收到你的留言，我们会尽快回复你！");
        form.reset();
      } else {
        setStatus("error");
        setMessage(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "提交失败，请稍后再试，或检查填写的内容。"
        );
      }
    } catch {
      setStatus("error");
      setMessage("网络开小差了，请稍后重试。");
    }
  };

  return (
    <section id="contact" className="bg-background-100 py-14 md:py-16">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <span className="text-sm font-medium text-primary-600">CONTACT</span>
            <h2 className="font-heading mt-1 text-2xl text-foreground-900 md:text-3xl">联系我们</h2>
            <p className="mt-3 max-w-md text-sm leading-7 text-foreground-600">
              对剧情有不一样的解读？发现了隐藏的彩蛋？或者对网站内容有建议？
              欢迎留言告诉我们，我们会认真阅读每一条反馈，让这份指南越来越完善。
            </p>

            <div className="mt-8 space-y-4">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                  <i className="ri-mail-line text-lg"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">邮箱</p>
                  <p className="text-sm text-foreground-800">hello@mimijiaoxue.example</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-accent-700">
                  <i className="ri-chat-3-line text-lg"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">反馈主题</p>
                  <p className="text-sm text-foreground-800">剧情解读 · 分集补充 · 彩蛋发现 · 站务建议</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                  <i className="ri-time-line text-lg"></i>
                </span>
                <div>
                  <p className="text-xs text-foreground-500">回复时间</p>
                  <p className="text-sm text-foreground-800">通常在 1–3 个工作日内回复</p>
                </div>
              </div>
            </div>
          </div>

          <form
            data-readdy-form="秘密教学动漫联系表单"
            onSubmit={onSubmit}
            className="rounded-lg border border-background-200 bg-background-50 p-6 md:p-7"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="name" className="text-sm text-foreground-700">称呼</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  placeholder="你的昵称"
                  className="h-10 rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-sm text-foreground-700">邮箱</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="用于接收回复"
                  className="h-10 rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300"
                />
              </div>
            </div>

            <div className="mt-4 flex flex-col gap-1.5">
              <label htmlFor="subject" className="text-sm text-foreground-700">反馈主题</label>
              <select
                id="subject"
                name="subject"
                className="h-10 rounded-md border border-background-200 bg-background-50 px-3 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300"
              >
                <option value="剧情解读">剧情解读</option>
                <option value="分集补充">分集补充</option>
                <option value="彩蛋发现">彩蛋发现</option>
                <option value="站务建议">站务建议</option>
              </select>
            </div>

            <div className="mt-4 flex flex-col gap-1.5">
              <label htmlFor="message" className="text-sm text-foreground-700">留言内容</label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="想说的话（最多 500 字）"
                className="w-full rounded-md border border-background-200 bg-background-50 px-3 py-2.5 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-300"
              ></textarea>
            </div>

            <div className="honeypot-field" aria-hidden="true">
              <label htmlFor="contact_alt">请勿填写此栏</label>
              <input
                id="contact_alt"
                name="contact_alt"
                type="text"
                tabIndex={-1}
                autoComplete="off"
                readOnly
              />
            </div>

            {status !== "idle" && (
              <div
                className={`mt-4 flex items-center gap-2 rounded-md px-4 py-3 text-sm ${
                  status === "success"
                    ? "bg-primary-50 text-primary-700"
                    : status === "error"
                    ? "bg-secondary-100 text-secondary-800"
                    : "bg-background-100 text-foreground-600"
                }`}
              >
                <i className={status === "error" ? "ri-error-warning-line" : status === "success" ? "ri-checkbox-circle-line" : "ri-loader-4-line animate-spin"}></i>
                <span>{message}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={status === "sending"}
              className="mt-5 inline-flex w-full items-center justify-center whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:opacity-60 cursor-pointer"
            >
              {status === "sending" ? "提交中…" : "提交留言"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}